
import { defineComponent, onBeforeMount, reactive } from 'vue'

// components
import NewInput from '@/ui-kit/NewInput/index.vue'
import NewSelect from '@/ui-kit/NewSelect/index.vue'
import NewButton from '@/ui-kit/NewButton/index.vue'

// types
import type { CityT } from '@/core/types/cities.types'
import type { UserT } from '@/core/types/users.types'
import type { GetTripsAnalyticsParams } from '@/new-services/types/tms'

// realisations
import { users } from '@/core/realisations/users.realisation'

// new-services
import locationAPI from '@/new-services/location.service'

// composable
import useVuelidate from '@vuelidate/core'
import { required } from '@vuelidate/validators'

// constants
const UTILIZATION_OPTIONS = [
    { id: 1, name: 'Утилизация 100%' },
    { id: 2, name: 'Утилизация меньше 100%' },
    { id: 3, name: 'Утилизация больше 100%' },
]

export default defineComponent({
    components: {
        NewInput,
        NewSelect,
        NewButton
    },
    props: {
        loading: {
            type: Boolean,
            default: false,
        }
    },
    emits: ['submit'],
    setup(props, { emit }) {
        const state = reactive({
            tripNumber: null as string | null,
            fromCityId: null as number | null,
            toCityId: null as number | null,
            logistId: null as number | null,
            departureDateFrom: null as string | null,
            departureDateTo: null as string | null,
            arrivalDateFrom: null as string | null,
            arrivalDateTo: null as string | null,
            utilizationIds: [] as Array<number>,
        })

        const state_form$ = useVuelidate({
            arrivalDateFrom: { required },
            arrivalDateTo: { required },
        }, state)

        const data_state = reactive({
            cities: {
                is_loading: false,
                is_failed: false,
                list: [] as Array<CityT>
            },
            logists: {
                is_loading: false,
                is_failed: false,
                list: [] as Array<UserT>
            }
        })

        const loadCities = async () => {
            try {
                data_state.cities.is_loading = true

                const response = await locationAPI.getCities()
                data_state.cities.list = response.data.data

                data_state.cities.is_failed = false
            } catch (error) {
                data_state.cities.is_failed = true
                console.error(error)
            } finally {
                data_state.cities.is_loading = false
            }
        }

        const loadLogists = async () => {
            try {
                data_state.logists.is_loading = true

                const response = await users.getLogists(1, undefined, 0, undefined, 9999)
                data_state.logists.list = response.data

                data_state.logists.is_failed = false
            } catch (error) {
                data_state.logists.is_failed = true
                console.error(error)
            } finally {
                data_state.logists.is_loading = false
            }
        }

        const formatDate = (date: Date) => {
            const year = date.getFullYear()
            const month = String(date.getMonth() + 1).padStart(2, '0')
            const day = String(date.getDate()).padStart(2, '0')
            return `${year}-${month}-${day}`
        }

        const initializeDefaultDates = (with_submit?: boolean) => {
            const currentDate = new Date()

            const previousMonthDate = new Date(currentDate)
            previousMonthDate.setMonth(previousMonthDate.getMonth() - 1)

            state.arrivalDateFrom = formatDate(previousMonthDate)
            state.arrivalDateTo = formatDate(currentDate)

            if (with_submit) {
                onSubmit()
            }
        }

        onBeforeMount(() => {
            initializeDefaultDates(true)
            loadCities()
            loadLogists()
        })

        const onSubmit = () => {
            if (props.loading) return

            state_form$.value.$touch()
            if (state_form$.value.$invalid) return

            const params: GetTripsAnalyticsParams = {
                tripNumber: state.tripNumber,
                fromCityId: state.fromCityId,
                toCityId: state.toCityId,
                logistId: state.logistId,
                departureDateFrom: state.departureDateFrom as string,
                departureDateTo: state.departureDateTo as string,
                arrivalDateFrom: state.arrivalDateFrom as string,
                arrivalDateTo: state.arrivalDateTo as string,
                utilizationIds: state.utilizationIds
            }

            emit('submit', params)
        }

        const onReset = () => {
            if (props.loading) return

            state.departureDateFrom = null
            state.departureDateTo = null
            state.tripNumber = null
            state.fromCityId = null
            state.toCityId = null
            state.logistId = null
            state.utilizationIds = []

            initializeDefaultDates(true)
        }

        return {
            UTILIZATION_OPTIONS,
            state,
            state_form$,
            data_state,
            loadCities,
            loadLogists,
            onSubmit,
            onReset
        }
    }
})
